import { Helmet } from 'react-helmet-async';
import { AgGridReact } from 'ag-grid-react';
import { useState, useEffect, useRef, useMemo } from 'react';
import { enqueueSnackbar } from 'notistack';
import { Box, Button, CircularProgress, Stack, Typography, IconButton } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, MailOutlined } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axiosInstance from '../../utils/axios';
import EditIconButton from '../../reuseable-components/DefaultButtons/EditIconButton';
import Iconify from '../../reuseable-components/iconify/Iconify';
import AgGridDefaults from '../../reuseable-components/AGGrid/AGGridDefaults';
import AGGridQuickFilterWrapper from '../../reuseable-components/AGGrid/AGGridQuickFilterWrapper';
import { AGGridDeleteCol, DeleteStartButton, DeleteSubmitButton } from '../../reuseable-components/AGGrid/AGGridDeleteCol';
import InvoiceForm from './InvoiceForm';
import autoSizeColumns from '../../reuseable-components/AGGrid/AGGridAutoSizeCols';
import { AGGridDateFilter } from '../../reuseable-components/AGGrid/AGGridFilters';
import { AGGridCheckboxColDefaults } from '../../reuseable-components/AGGrid/AGGridCheckboxColDefaults';
import { objectToFormData } from '../../utils/objectToFormData';
import AGGridIdSearch from '../../reuseable-components/AGGrid/AGGridIdSearch';

export default function InvoiceList() {
  const gridRef = useRef(null);
  const navigate = useNavigate();
  const { state } = useLocation();
  const facilityId = state?.facilityId;
  const facilityName = state?.facilityName;
  const [invoices, setInvoices] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [editInvoice, setEditInvoice] = useState(null);
  const [openEditInvoiceForm, setOpenEditInvoiceForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [startDeletingItems, setStartDeletingItems] = useState(false);
  const [startResendingItems, setStartResendingItems] = useState(false);
  const [submitingInvoiceResend, setSubmitingInvoiceResend] = useState(false);
  const [gridIdSearch] = useState(AGGridIdSearch((data) => { setEditInvoice(data); setOpenEditInvoiceForm(true); }));

  useEffect(() => {
    axiosInstance.get(facilityId ? `/invoice/invoices?id=${facilityId}` : "/invoice/invoices")
      .then(res => {
        const facilityObj = res.data.facilities.reduce((acc, facility) => { acc[facility.value] =  facility.label; return acc; }, {});
        const vendorObj = res.data.vendors.reduce((acc, vendor) => { acc[vendor.value] = vendor.label; return acc; }, {});;
        setInvoices(res.data.invoices.map(invoice => ({ ...invoice, facility: facilityObj[invoice.facilityId], vendor: vendorObj[invoice.vendorId] }))
          .sort((invoice, nextInvoice) => {
            if (invoice.booked !== nextInvoice.booked) return invoice.booked ? 1 : -1;
            if (invoice.invoiceDate !== nextInvoice.invoiceDate) return invoice.invoiceDate > nextInvoice.invoiceDate ? 1 : -1;
            return 0;
          }));
        setFacilities(res.data.facilities);
        setVendors(res.data.vendors);
      })
      .catch(() => enqueueSnackbar('Invoices list failed', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, [facilityId]);

  const resendInvoices = () => {
    setSubmitingInvoiceResend(true);
    axiosInstance.post("/invoice/resendinvoices", objectToFormData({ ids: invoices.filter(x => x.selectedForResend).map(x => x.id) }))
      .then(() => enqueueSnackbar("Invoices queued to be resent. You will receive an email with the results.", { variant: "success" }))
      .catch(() => enqueueSnackbar("Failed to resend invoices", { variant: "error" }))
      .finally(() => {
        setInvoices(currData => { currData.filter(x => x.selectedForResend).forEach(x => { x.selectedForResend = false }); return currData; });
        setStartResendingItems(false);
        setSubmitingInvoiceResend(false);
      });
  }

  const columns = useMemo(() => [
    {
      field: 'id',
      headerName: 'Id',
      filter: true
    },
    {
      field: 'orderNumber',
      headerName: 'Order#',
      filter: true
    },
    {
      field: 'customerOrderNumber',
      headerName: 'Customer PO#',
      filter: true
    },
    {
      field: 'vendorInvoiceId',
      headerName: 'Vendor Id',
      cellRenderer: (params) => <Box>
        {params.data.vendorBillURLId ? <a href={`https://qbo.intuit.com/app/bill?txnId=${params.data.vendorBillURLId}`} rel="noreferrer" target="_blank">
          {params.data.vendorInvoiceId}
        </a> : params.data.vendorInvoiceId}
      </Box>,
      minWidth: 100,
      flex: 1,
      filter: true
    },
    {
      field: 'quickbooksInvoiceId',
      headerName: 'Customer Id',
      cellRenderer: (params) => <Box>
        {params.data.customerInvoiceURLId ? <a href={`https://qbo.intuit.com/app/invoice?txnId=${params.data.customerInvoiceURLId}`} rel="noreferrer" target="_blank">
          {params.data.quickbooksInvoiceId}
        </a> : params.data.quickbooksInvoiceId}
      </Box>,
      minWidth: 100,
      filter: true
    },
    {
      field: 'invoiceDate',
      cellRenderer: (params) => new Date(params.value).toLocaleDateString(),
      ...AGGridDateFilter()
    },
    {
      field: 'total',
      cellRenderer: (params) => params.value.toLocaleString('en-US', { style: 'currency', currency: 'USD' }),
      valueGetter: (params) => Number(params.data.total),
      filter: "agNumberColumnFilter",
    },
    {
      field: 'facility',
      cellRenderer: (params) => params.value ?? params.sentTo,
      maxWidth: 300,
      filter: true
    },
    {
      field: 'vendor',
      filter: true
    },
    !startDeletingItems && !startResendingItems ? {
      field: 'booked',
      cellRenderer: (params) => params.value ? <CheckCircle color="primary" sx={{ mt: 1 }} /> : "",
      filter: true
    } : null,
    {
      field: 'error',
      maxWidth: 200,
      filter: true
    },
    startDeletingItems ? AGGridDeleteCol(setInvoices) : null,
    startResendingItems ? AGGridCheckboxColDefaults(setInvoices, 'selectedForResend', 'Resend') : null,
    !startDeletingItems && !startResendingItems ? {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      cellRenderer: (params) => <Stack direction="row" spacing={1}>
        <EditIconButton onClick={() => { setEditInvoice(params.data); setOpenEditInvoiceForm(true); }} />
      </Stack>
    } : null
  ].filter(col => col), [startDeletingItems, startResendingItems]);

  return (<>
    <Helmet>
      <title>Invoice List | NHIDirect</title>
    </Helmet>

    <Box sx={{ height: "calc(100% - 55px)", mt: 2 }}>
      <AGGridQuickFilterWrapper gridRef={gridRef}>
        {facilityId && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={() => navigate(-1)} size="small" sx={{ color: 'black', "&:hover": { backgroundColor: "transparent", } }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography fontWeight={600}>{facilityName}</Typography>
          </Box> )}
        <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={() => setOpenEditInvoiceForm(true)} color="primary" size="small">
          <Typography>New Invoice</Typography>
        </Button>
        <Box sx={{ width: { xs: '100%' }, marginTop: "auto" }}>
          <Button variant="contained" color="primary" startIcon={<MailOutlined />} sx={{ marginTop: "auto" }} size="small"
            onClick={() => {
              setStartResendingItems(!startResendingItems);
              setStartDeletingItems(false);
              if (startResendingItems) setInvoices(data => data.map(x => ({ ...x, selectedForResend: false })))
            }}>
            <Typography>{startResendingItems ? "Cancel Resend" : "Select Invoices To Resend"}</Typography>
          </Button>
        </Box>
        {startResendingItems && <Box sx={{ width: { xs: '100%' }, marginTop: "auto" }}>
          <LoadingButton loading={submitingInvoiceResend} variant="contained" color="secondary" startIcon={<MailOutlined />}
            sx={{ marginTop: "auto" }} size="small" onClick={resendInvoices}>
            <Typography>Resend Invoices</Typography>
          </LoadingButton>
        </Box>}
        <Box sx={{ width: { xs: '100%' }, marginTop: "auto" }}>
          <DeleteStartButton setStartDeletingItems={(val) => { setStartDeletingItems(val); setStartResendingItems(false) }}
            startDeletingItems={startDeletingItems} setData={setInvoices} />
        </Box>
        {startDeletingItems && <Box sx={{ width: { xs: '100%' }, marginTop: "auto" }}>
          <DeleteSubmitButton setStartDeletingItems={setStartDeletingItems} setData={setInvoices} data={invoices} url="/invoice/deleteInvoices" />
        </Box>}
      </AGGridQuickFilterWrapper>
      {!loading ? <Box sx={{ height: "max(500px, 100%)" }}>
        <AgGridReact ref={gridRef} {...AgGridDefaults} {...gridIdSearch} columnDefs={columns} rowData={invoices} loading={loading} onNewColumnsLoaded={autoSizeColumns} />
      </Box> : <Box sx={{ display: "flex", justifyContent: "center", m: 3 }}><CircularProgress /></Box>}
      <InvoiceForm editInvoice={editInvoice} setInvoices={setInvoices} open={openEditInvoiceForm} close={() => { setOpenEditInvoiceForm(false); setEditInvoice(null); }}
        facilities={facilities} vendors={vendors} />
    </Box>
  </>);
}
